import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardActionArea, CardContent, CircularProgress, Container, Link, Stack, TextField, Typography } from '@mui/material';
import { createSession, listQuizzes } from './api';
import { useAsync } from './useAsync';

function HostHome() {
    const navigate = useNavigate();
    const { data: quizzes, error: loadError } = useAsync(listQuizzes, 'Could not load the quiz catalog. Is the backend running?');
    const [selected, setSelected] = useState(null);
    const [name, setName] = useState('');
    const [error, setError] = useState(null);
    const [creating, setCreating] = useState(false);

    const trimmedName = name.trim();
    const displayError = error || loadError;

    const generate = async () => {
        setCreating(true);
        setError(null);
        try {
            const session = await createSession(selected, trimmedName);
            navigate(`/live/host/${session.sessionId}`);
        } catch {
            setError('Could not create the session.');
            setCreating(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ py: 6 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>Start a live quiz</Typography>
                <Link component={RouterLink} to="/live/host/sessions" underline="hover">Past sessions</Link>
            </Stack>
            <Typography color="text.secondary" sx={{ mb: 4 }}>Pick a quiz, then show the QR code to the room.</Typography>

            {displayError && <Alert severity="error" sx={{ mb: 3 }}>{displayError}</Alert>}

            <TextField
                label="Session name"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ mb: 3 }}
            />

            {!quizzes && !displayError && <CircularProgress />}

            <Stack spacing={2}>
                {quizzes?.map((quiz) => (
                    <Card
                        key={quiz.slug}
                        variant="outlined"
                        sx={{ borderColor: selected === quiz.slug ? 'primary.main' : undefined, borderWidth: selected === quiz.slug ? 2 : 1 }}
                    >
                        <CardActionArea onClick={() => setSelected(quiz.slug)}>
                            <CardContent>
                                <Typography sx={{ fontWeight: 700 }}>{quiz.title}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {quiz.questionCount} questions · {quiz.slug}
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                ))}
            </Stack>

            {quizzes?.length === 0 && <Alert severity="info">No quizzes found in backend/quizzes/.</Alert>}

            <Box sx={{ mt: 4 }}>
                <Button variant="contained" size="large" disabled={!selected || !trimmedName || creating} onClick={generate}>
                    {creating ? 'Creating…' : 'Generate QR'}
                </Button>
            </Box>
        </Container>
    );
}

export default HostHome;
